import React, { useState, useEffect }from 'react';
import { fetchDeniedTags, addDeniedTag, removeDeniedTag } from '../services/adminApi';

export default function AdminDeniedTagsPage({ embedded = false }) {
  const [deniedTags, setDeniedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Form state
  const [tagInput, setTagInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [formError, setFormError] = useState(null);

  // Form state for pagination
  const [limit, setLimit] = useState(1000);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadDeniedTags();
  }, [limit, offset]);

  async function loadDeniedTags() {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchDeniedTags(limit, offset);
      /* c8 ignore start */
      setDeniedTags(res.deniedTags || []);
      setTotal(res.pagination?.total || 0);
      /* c8 ignore stop */
    /* c8 ignore start */
    } catch (e) {
      setError(e.message);
      setDeniedTags([]);
    } finally {
    /* c8 ignore stop */
      setLoading(false);
    }
  }

  async function handleAddTag(e) {
    e.preventDefault();
    setFormError(null);

    if (!tagInput.trim()) {
      setFormError('Tag cannot be empty');
      return;
    }

    let formEl = null;
    try {
      // Keep a reference to the form element so we can synchronously
      // disable controls while the async request is in-flight. This
      // ensures tests (and users) see the disabled state immediately.
      /* c8 ignore start */
      formEl = e.currentTarget || e.target;
      /* c8 ignore stop */

      setSaving(true);
      if (formEl && typeof formEl.querySelectorAll === 'function') {
        const els = formEl.querySelectorAll('input, textarea, button[type="submit"]');
        els.forEach((el) => { try { el.disabled = true; } catch (err) {} });
      }

      const normalizedTag = tagInput.trim().toLowerCase();
      const res = await addDeniedTag(normalizedTag, reasonInput.trim() || null);

      // Insert new tag at the beginning of the list
      setDeniedTags([res.deniedTag, ...deniedTags]);
      setTotal(total + 1);
      setTagInput('');
      setReasonInput('');
    } catch (e) {
      if (e.message.includes('409') || e.message.includes('already')) {
        setFormError(`Tag '${tagInput}' is already in the denied list`);
      } else {
        setFormError(e.message);
      }
    } finally {
      setSaving(false);
      // Re-enable form controls (if still present)
      /* c8 ignore start */
      try {
        if (formEl && typeof formEl.querySelectorAll === 'function') {
          const els = formEl.querySelectorAll('input, textarea, button[type="submit"]');
          els.forEach((el) => { try { el.disabled = false; } catch (err) {} });
        }
      } catch (err) {
        // ignore
      }
      /* c8 ignore stop */
    }
  }

  async function handleDeleteTag(id, tag) {
    if (!window.confirm(`Are you sure you want to remove the denied tag '${tag}'?`)) {
      return;
    }

    try {
      setDeleting(id);
      await removeDeniedTag(id);
      setDeniedTags(deniedTags.filter(t => t.id !== id));
      setTotal(Math.max(0, total - 1));
    } catch (e) {
      alert(`Failed to remove tag: ${e.message}`);
    } finally {
      setDeleting(null);
    }
  }

  const pages = Math.ceil(total / limit) || 1;
  const currentPage = Math.floor(offset / limit) + 1;

  const content = (
    <>
      <h2 className="text-2xl font-bold mb-6">Denied Tags</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-900 text-red-100 rounded-lg">
          Error: {error}
        </div>
      )}

      {/* Add New Tag Form */}
      <div className="bg-dark-800 border border-dark-600 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Add New Denied Tag</h3>
        
        <form onSubmit={handleAddTag} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Tag *</label>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Enter tag to deny"
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              disabled={saving}
            />
            <p className="text-xs text-gray-400 mt-1">Will be auto-lowercased</p>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Reason (Optional)</label>
            <textarea
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              placeholder="Why is this tag denied?"
              rows={3}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              disabled={saving}
            />
          </div>

          {formError && (
            <div className="p-3 bg-red-900 text-red-100 rounded-lg text-sm">
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding...' : 'Add Tag'}
          </button>
        </form>
      </div>

      {/* Denied Tags Table */}
      <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-dark-600">
          <h3 className="text-lg font-semibold">Denied Tags ({total})</h3>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-400">Loading...</div>
        ) : deniedTags.length === 0 ? (
          <div className="p-6 text-center text-gray-400">No denied tags yet</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-600 bg-dark-900">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-300">Tag</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-300">Reason</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-300">Added By</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-300">Date</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-300">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deniedTags.map((tag) => (
                    <tr key={tag.id} className="border-b border-dark-600 hover:bg-dark-700 transition">
                      <td className="px-6 py-4 text-sm font-mono text-gray-100">{tag.tag}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {tag.reason ? (
                          <span title={tag.reason} className="line-clamp-1">
                            {tag.reason}
                          </span>
                        ) : (
                          <span className="text-gray-500 italic">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{tag.created_by || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {tag.created_at ? new Date(tag.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDeleteTag(tag.id, tag.tag)}
                          disabled={deleting === tag.id}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleting === tag.id ? 'Removing...' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-dark-600 flex items-center justify-between text-sm text-gray-300">
              <div>
                Page <span className="font-semibold">{currentPage}</span> of{' '}
                <span className="font-semibold">{pages}</span>
              </div>
              <div className="space-x-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0 || loading}
                  className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>
                <button
                  onClick={() =>
                    setOffset(
                      Math.min(offset + limit, (pages - 1) * limit)
                    )
                  }
                  disabled={currentPage >= pages || loading}
                  className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );

  if (embedded) return content;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {content}
    </div>
  );
}
